/**
 * Hebrew reference helpers — single source of truth for displaying
 * Tanakh / Mishnah / Talmud / Rabbinic references in their Hebrew form.
 *
 * Every visible reference in the UI must go through here. We never show
 * raw English book names like "Leviticus 26:46" anywhere a user can see.
 * The system may STORE English (Sefaria's canonical slug) but the DISPLAY
 * is always Hebrew.
 *
 * Coverage:
 *   • All 39 Tanakh books
 *   • Mishnah tractates (Berakhot, Pesachim, Yoma, …)
 *   • Talmud Bavli (same names, just with daf+amud like "5a"/"5b")
 *   • A few common works (Pirkei Avot, Mishneh Torah)
 *   • Verse ranges: "Ruth 1:16-17" → "רות א׳:ט״ז-י״ז"
 *   • Talmud daf: "Berakhot 5b" → "ברכות ה ע״ב"
 *   • Pass-through for anything we can't parse, so an unrecognised
 *     ref renders raw rather than silently disappearing.
 */

// English Sefaria slug → Hebrew name (canonical Tanakh books).
// Mirrors backend tanakh_data.py exactly.
export const BOOK_HE = {
  // Torah
  Genesis: "בראשית",
  Exodus: "שמות",
  Leviticus: "ויקרא",
  Numbers: "במדבר",
  Deuteronomy: "דברים",
  // Nevi'im
  Joshua: "יהושע",
  Judges: "שופטים",
  "I Samuel": "שמואל א",
  "II Samuel": "שמואל ב",
  "1 Samuel": "שמואל א",
  "2 Samuel": "שמואל ב",
  "I Kings": "מלכים א",
  "II Kings": "מלכים ב",
  "1 Kings": "מלכים א",
  "2 Kings": "מלכים ב",
  Isaiah: "ישעיה",
  Jeremiah: "ירמיה",
  Ezekiel: "יחזקאל",
  Hosea: "הושע",
  Joel: "יואל",
  Amos: "עמוס",
  Obadiah: "עובדיה",
  Jonah: "יונה",
  Micah: "מיכה",
  Nahum: "נחום",
  Habakkuk: "חבקוק",
  Zephaniah: "צפניה",
  Haggai: "חגי",
  Zechariah: "זכריה",
  Malachi: "מלאכי",
  // Ketuvim
  Psalms: "תהילים",
  Proverbs: "משלי",
  Job: "איוב",
  "Song of Songs": "שיר השירים",
  Ruth: "רות",
  Lamentations: "איכה",
  Ecclesiastes: "קהלת",
  Esther: "אסתר",
  Daniel: "דניאל",
  Ezra: "עזרא",
  Nehemiah: "נחמיה",
  "I Chronicles": "דברי הימים א",
  "II Chronicles": "דברי הימים ב",
  "1 Chronicles": "דברי הימים א",
  "2 Chronicles": "דברי הימים ב",
};

// Talmud / Mishnah tractates the LLM is likely to surface. Same Hebrew
// spelling for Bavli + Mishnah — the page suffix (5a/5b) tells the user
// it's Talmud; an integer chapter:mishnah pair means Mishnah.
//
// We keep this list intentionally narrow to the tractates Chavruta is
// likely to cite — it can be expanded any time without touching callers.
export const TRACTATE_HE = {
  // === Zera'im (סדר זרעים) — 11 ===
  Berakhot: "ברכות",
  Peah: "פאה",
  Demai: "דמאי",
  Kilayim: "כלאיים",
  Sheviit: "שביעית",
  Terumot: "תרומות",
  Maasrot: "מעשרות",
  "Maaser Sheni": "מעשר שני",
  Challah: "חלה",
  Orlah: "ערלה",
  Bikkurim: "ביכורים",
  // === Mo'ed (סדר מועד) — 12 ===
  Shabbat: "שבת",
  Eruvin: "עירובין",
  Pesachim: "פסחים",
  Shekalim: "שקלים",
  Yoma: "יומא",
  Sukkah: "סוכה",
  Beitzah: "ביצה",
  "Rosh Hashanah": "ראש השנה",
  Taanit: "תענית",
  Megillah: "מגילה",
  "Moed Katan": "מועד קטן",
  Chagigah: "חגיגה",
  // === Nashim (סדר נשים) — 7 ===
  Yevamot: "יבמות",
  Ketubot: "כתובות",
  Nedarim: "נדרים",
  Nazir: "נזיר",
  Sotah: "סוטה",
  Gittin: "גיטין",
  Kiddushin: "קידושין",
  // === Nezikin (סדר נזיקין) — 10 ===
  "Bava Kamma": "בבא קמא",
  "Bava Metzia": "בבא מציעא",
  "Bava Batra": "בבא בתרא",
  Sanhedrin: "סנהדרין",
  Makkot: "מכות",
  Shevuot: "שבועות",
  Eduyot: "עדויות",
  "Avodah Zarah": "עבודה זרה",
  // Pirkei Avot is Sefaria's canonical slug (no "Mishnah" prefix);
  // we still accept "Avot" as a shorthand the LLM/users may emit.
  "Pirkei Avot": "פרקי אבות",
  Avot: "אבות",
  Horayot: "הוריות",
  // === Kodashim (סדר קדשים) — 11 ===
  Zevachim: "זבחים",
  Menachot: "מנחות",
  Chullin: "חולין",
  Bekhorot: "בכורות",
  Arakhin: "ערכין",
  Temurah: "תמורה",
  Keritot: "כרתות",
  Meilah: "מעילה",
  Tamid: "תמיד",
  Middot: "מידות",
  Kinnim: "קינים",
  // === Tahorot (סדר טהרות) — 12 ===
  Kelim: "כלים",
  Oholot: "אהלות",
  Negaim: "נגעים",
  Parah: "פרה",
  Tahorot: "טהרות",
  Mikvaot: "מקוואות",
  Niddah: "נדה",
  Makhshirin: "מכשירין",
  Zavim: "זבים",
  "Tevul Yom": "טבול יום",
  Yadayim: "ידיים",
  Oktzin: "עוקצין",
};

// Other rabbinic works people commonly cite. The LLM in stage A
// proposes things like "Mishneh Torah, Repentance 2:1" or "Tanya".
// We treat the part before any comma as the work-name and leave the
// section/chapter spec for Hebrew gematria conversion.
export const WORK_HE = {
  "Mishneh Torah": "משנה תורה",
  "Shulchan Arukh": "שולחן ערוך",
  Zohar: "זוהר",
  Tanya: "תניא",
  "Mesillat Yesharim": "מסילת ישרים",
  "Pirkei DeRabbi Eliezer": "פרקי דרבי אליעזר",
  "Avot DeRabbi Natan": "אבות דרבי נתן",
  "Bereshit Rabbah": "בראשית רבה",
  "Shemot Rabbah": "שמות רבה",
  "Vayikra Rabbah": "ויקרא רבה",
  "Bamidbar Rabbah": "במדבר רבה",
  "Devarim Rabbah": "דברים רבה",
  "Midrash Tanchuma": "מדרש תנחומא",
  Mekhilta: "מכילתא",
  Sifra: "ספרא",
  Sifrei: "ספרי",
};

/**
 * Convert a positive integer to Hebrew gematria with proper geresh / gershayim.
 *   1   → "א׳"
 *   15  → "ט״ו"  (yud-vav avoidance — kedushas hashem)
 *   16  → "ט״ז"  (yud-heh avoidance — kedushas hashem)
 *   46  → "מ״ו"
 *   100 → "ק׳"
 */
export function numberToHebrew(n) {
  if (n == null || n < 1) return "";
  const map = [
    [400, "ת"], [300, "ש"], [200, "ר"], [100, "ק"],
    [90, "צ"], [80, "פ"], [70, "ע"], [60, "ס"], [50, "נ"],
    [40, "מ"], [30, "ל"], [20, "כ"], [10, "י"],
    [9, "ט"], [8, "ח"], [7, "ז"], [6, "ו"], [5, "ה"],
    [4, "ד"], [3, "ג"], [2, "ב"], [1, "א"],
  ];
  let remaining = n;
  let out = "";
  for (const [val, ch] of map) {
    while (remaining >= val) {
      out += ch;
      remaining -= val;
    }
  }
  // Replace divine-name pairs at the END of each two-digit run.
  out = out.replace(/יה$/, "טו").replace(/יו$/, "טז");
  out = out.replace(/יה(?=[^א-ת]|$)/, "טו").replace(/יו(?=[^א-ת]|$)/, "טז");
  if (out.length > 1) out = out.slice(0, -1) + "\u05F4" + out.slice(-1);
  else if (out.length === 1) out = out + "\u05F3";
  return out;
}

/**
 * Resolve the Hebrew display name for a book/tractate.
 * Accepts either the English slug or an already-Hebrew name (passes through).
 */
/**
 * Normalise a Sefaria-style English book name into the casing the
 * BOOK_HE / TRACTATE_HE maps expect:
 *   "genesis"            → "Genesis"
 *   "song of songs"      → "Song of Songs"
 *   "ii chronicles"      → "II Chronicles"
 *   "mishnah berakhot"   → "Mishnah Berakhot"
 *
 * Roman numerals (i, ii, iii) are uppercased; the word "of" stays
 * lowercase (Sefaria's house style for compound book names).
 */
function _titleCaseSefariaName(s) {
  return s
    .split(/\s+/)
    .map((w, idx) => {
      if (/^i+$/i.test(w)) return w.toUpperCase();
      if (idx > 0 && w.toLowerCase() === "of") return "of";
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
}

export function bookHe(book) {
  if (!book) return "";
  // Fast path: exact match (the LLM / API usually gives us canonical
  // Sefaria casing).
  if (BOOK_HE[book]) return BOOK_HE[book];
  if (TRACTATE_HE[book]) return TRACTATE_HE[book];
  if (WORK_HE[book]) return WORK_HE[book];

  // Tolerant path: accept lowercase / URL-slug input so the Reader
  // doesn't briefly flash the raw English slug between mount and the
  // moment the /api/books response arrives.  e.g. urlToSlug("genesis")
  // → "genesis", which without this branch would fall through and the
  // user would see "GENESIS" / "mishnah berakhot" for a split second.
  // We also normalise hyphens → spaces here so direct URL-slug input
  // ("berakhot-bavli") is accepted without a prior urlToSlug() call.
  const titled = _titleCaseSefariaName(book.replace(/-/g, " "));
  if (BOOK_HE[titled]) return BOOK_HE[titled];
  if (TRACTATE_HE[titled]) return TRACTATE_HE[titled];
  if (WORK_HE[titled]) return WORK_HE[titled];

  // Mishnah-prefixed slugs (e.g. "mishnah berakhot" → "מסכת ברכות").
  // Sefaria's Pirkei Avot is the lone tractate without the "Mishnah"
  // prefix; everything else in the Mishnah catalog follows the
  // "Mishnah {Tractate}" pattern.
  const mishnahMatch = titled.match(/^Mishnah\s+(.+)$/);
  if (mishnahMatch && TRACTATE_HE[mishnahMatch[1]]) {
    return "מסכת " + TRACTATE_HE[mishnahMatch[1]];
  }

  // Talmud Bavli slugs — URL form is "berakhot-bavli", so the title-cased
  // form here is "Berakhot Bavli". We strip the Bavli suffix and look up
  // the tractate name (which lives in the same TRACTATE_HE map, since
  // Sefaria gives the Mishnah and Bavli tractates identical canonical
  // names — only the work changes).
  const talmudMatch = titled.match(/^(.+?)\s+(Bavli|Talmud)$/i);
  if (talmudMatch && TRACTATE_HE[talmudMatch[1]]) {
    return "מסכת " + TRACTATE_HE[talmudMatch[1]];
  }

  return book;
}


/**
 * Synchronously decide whether a slug/ref points at a Mishnah tractate,
 * without waiting for the /api/books response.  Used by the Reader so
 * the cover paints in its correct teal palette from frame ONE — no
 * navy-blue flash before the API replies.
 *
 * Accepts any of:
 *   "mishnah-berakhot"       (URL slug)
 *   "mishnah berakhot"       (post-urlToSlug)
 *   "Mishnah Berakhot"       (canonical Sefaria)
 *   "pirkei-avot"            (the lone tractate without the Mishnah prefix)
 *   "Pirkei Avot"            (canonical)
 *
 * Anything else → false (Tanakh / Siddur / unknown → use the default
 * navy palette, which is correct for them).
 */
export function isMishnahSlug(slug) {
  if (!slug) return false;
  // Normalise: lower-case + collapse any hyphens to spaces so both URL
  // and post-urlToSlug forms hit the same branch.
  const s = String(slug).toLowerCase().replace(/-/g, " ").trim();
  if (s.startsWith("mishnah ")) return true;
  if (s === "pirkei avot" || s === "avot") return true;
  return false;
}

/**
 * Synchronously decide whether a slug/ref points at a Talmud Bavli
 * tractate. Mirror of isMishnahSlug() for the third corpus.
 *
 * Accepts:
 *   "berakhot-bavli"  (the URL slug we settled on — Bavli suffix
 *                      keeps the URL distinct from the Mishnah
 *                      tractate "Mishnah Berakhot")
 *   "berakhot bavli"  (post-urlToSlug)
 *   "Berakhot"        (canonical Sefaria — only matches when not also
 *                      shadowed by a Tanakh book. Safe today because
 *                      no Tanakh book is named "Berakhot".)
 */
export function isTalmudSlug(slug) {
  if (!slug) return false;
  const s = String(slug).toLowerCase().replace(/-/g, " ").trim();
  if (s.endsWith(" bavli") || s.endsWith(" talmud")) return true;
  // Bare canonical Sefaria slug for the currently-shipped tractate.
  // When we add more tractates, extend this set — keeping it explicit
  // means we never accidentally classify e.g. "Bekhorot" (Mishnah) as
  // Talmud.
  if (s === "berakhot") return true;
  return false;
}

/**
 * Convert a 1-indexed Talmud chapter to its **English** amud label
 * ("2a" / "2b" / "3a" ...). Mirror of `talmudDafLabel()` for the
 * English running-head inside the 3D book.
 */
export function talmudDafLabelEn(chapter) {
  if (!chapter || chapter < 1) return "";
  const daf = Math.floor((chapter - 1) / 2) + 2;
  const side = (chapter - 1) % 2 === 0 ? "a" : "b";
  return `Daf ${daf}${side}`;
}

/**
 * Convert a 1-indexed Talmud chapter to its display Hebrew label
 * ("דף ב׳ ע״א" / "דף ב׳ ע״ב" / "דף ג׳ ע״א" ...). The Talmud starts at
 * daf 2, amud a — chapter 1 in our flat numbering. Uses the existing
 * gematria converter for the daf number so the chrome stays consistent
 * with the rest of the reader.
 */
export function talmudDafLabel(chapter) {
  if (!chapter || chapter < 1) return "";
  const daf = Math.floor((chapter - 1) / 2) + 2;   // daf 2..64
  const amudKey = (chapter - 1) % 2 === 0 ? "א" : "ב";
  // We re-use intToHebrew where it exists in the module; if the caller
  // is outside this file they should still get a useful label. Inline
  // a tiny gematria here so this helper is self-contained.
  const daf_he = _smallGematria(daf);
  return `דף ${daf_he} ע״${amudKey}`;
}

function _smallGematria(n) {
  // Sufficient for daf numbers (2-200 covers every Bavli tractate).
  const ones = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  const tens = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
  const hundreds = ["", "ק", "ר", "ש", "ת", "תק", "תר", "תש", "תת", "תתק"];
  if (n <= 0) return String(n);
  let h = "";
  const c = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;
  if (c) h += hundreds[c];
  // Tens — special cases for 15 (טו) and 16 (טז) to avoid spelling
  // the Divine name.
  const last2 = (t * 10) + o;
  if (last2 === 15) h += "טו";
  else if (last2 === 16) h += "טז";
  else {
    if (t) h += tens[t];
    if (o) h += ones[o];
  }
  return h + "׳";
}

/**
 * Build the canonical Hebrew reference string for a Tanakh verse.
 *
 *   formatHebrewRef("Leviticus", 26, 46)        → "ויקרא כ״ו:מ״ו"
 *   formatHebrewRef("בראשית",     1,  1)        → "בראשית א׳:א׳"
 *   formatHebrewRef("Genesis",   1)             → "בראשית פרק א׳"
 *   formatHebrewRef(null, null, null)           → ""
 *
 * `style`:
 *   "auto"     (default) → "ספר ch:vs" or "ספר פרק ch" if no verse
 *   "verse"              → always "ספר ch:vs"
 *   "chapter-only"       → always "ספר פרק ch"
 *   "compact"            → "ספר ch:vs" with NO Hebrew numerals (e.g. "בראשית 1:1")
 */
export function formatHebrewRef(book, chapter, verse, style = "auto") {
  const he = bookHe(book);
  if (!he) return "";
  if (chapter == null) return he;
  if (style === "compact") {
    return verse != null ? `${he} ${chapter}:${verse}` : `${he} פרק ${chapter}`;
  }
  const chHe = numberToHebrew(chapter);
  if (verse == null) {
    return style === "verse" ? `${he} ${chHe}` : `${he} פרק ${chHe}`;
  }
  if (style === "chapter-only") return `${he} פרק ${chHe}`;
  return `${he} ${chHe}:${numberToHebrew(verse)}`;
}

// =====================================================================
// Sefaria-style ref string parsing — covers ranges, Talmud, rabbinic works
// =====================================================================

// Sorted longest-first so "Pirkei Avot" beats "Avot" on a prefix match.
const ALL_BOOK_KEYS = [
  ...Object.keys(BOOK_HE),
  ...Object.keys(TRACTATE_HE),
  ...Object.keys(WORK_HE),
].sort((a, b) => b.length - a.length);

function matchBookPrefix(s) {
  // Try each known book/tractate/work name as a literal prefix. Sorted
  // longest-first so "I Kings" wins over "Kings" and "Bava Kamma" wins
  // over "Bava".
  for (const key of ALL_BOOK_KEYS) {
    if (s === key) return { key, rest: "" };
    if (s.startsWith(key + " ")) return { key, rest: s.slice(key.length + 1) };
  }
  return null;
}

// Talmud daf — "5a" / "5b" / "12a" etc. Hebrew convention is "ה ע״א" / "ה ע״ב".
const DAF_RE = /^(\d+)([ab])$/i;

// Verse spec — "1", "1:2", "1:2-3", "1:2-2:5".
const VERSE_SPEC_RE = /^(\d+)(?::(\d+))?(?:-(\d+)(?::(\d+))?)?$/;

function formatVerseSpec(spec) {
  // Returns the Hebrew rendering of the verse spec, or `null` if it
  // doesn't fit the supported shapes (caller falls back to passthrough).
  const m = spec.match(VERSE_SPEC_RE);
  if (!m) return null;
  const c1 = parseInt(m[1], 10);
  const v1 = m[2] ? parseInt(m[2], 10) : null;
  const c2 = m[3] ? parseInt(m[3], 10) : null;
  const v2 = m[4] ? parseInt(m[4], 10) : null;
  if (v1 == null) {
    // "1" — single chapter
    if (c2 == null) return numberToHebrew(c1);
    // "1-3" — range of chapters
    return `${numberToHebrew(c1)}-${numberToHebrew(c2)}`;
  }
  if (c2 == null) {
    // "1:2" — single verse
    return `${numberToHebrew(c1)}:${numberToHebrew(v1)}`;
  }
  if (v2 == null) {
    // "1:2-3" — verse range within one chapter (c2 here is the
    // second VERSE per the regex shape).
    return `${numberToHebrew(c1)}:${numberToHebrew(v1)}-${numberToHebrew(c2)}`;
  }
  // "1:2-2:5" — multi-chapter verse range
  return `${numberToHebrew(c1)}:${numberToHebrew(v1)}-${numberToHebrew(c2)}:${numberToHebrew(v2)}`;
}

/**
 * Convert a Sefaria-style ref string to its Hebrew display form.
 *
 *   "Genesis 1:1"          → "בראשית א׳:א׳"
 *   "Numbers 7:25"         → "במדבר ז׳:כ״ה"
 *   "Ruth 1:16-17"         → "רות א׳:ט״ז-י״ז"
 *   "Ecclesiastes 3:1-8"   → "קהלת ג׳:א׳-ח׳"
 *   "Berakhot 5b"          → "ברכות ה ע״ב"
 *   "Pirkei Avot 4:1"      → "פרקי אבות ד׳:א׳"
 *   "Mishneh Torah, Repentance 2:1"
 *                          → "משנה תורה, Repentance ב׳:א׳"
 *                            (sub-section names stay English until we
 *                             add a translation map; the gematria still
 *                             converts so it's at least partly Hebrew)
 *   "" / null / unknown    → input as-is
 *
 * Accepts a string or an object `{book, chapter, verse}` — the object
 * form just delegates to `formatHebrewRef`.
 */
export function refToHebrew(refOrObj) {
  if (refOrObj == null) return "";
  if (typeof refOrObj === "object") {
    return formatHebrewRef(refOrObj.book, refOrObj.chapter, refOrObj.verse);
  }
  const raw = String(refOrObj).trim();
  if (!raw) return "";

  // Composite refs with a comma (e.g. "Mishneh Torah, Repentance 2:1"):
  // try to match the work-name prefix, then leave the sub-section as-is
  // (we don't ship a Mishneh-Torah section name dictionary yet) but
  // still convert any trailing chapter:verse via the normal path.
  const commaIdx = raw.indexOf(",");
  if (commaIdx > 0) {
    const head = raw.slice(0, commaIdx).trim();
    const tail = raw.slice(commaIdx + 1).trim();
    const m = matchBookPrefix(head);
    if (m) {
      // Try to peel a verse spec off the end of `tail` ("Repentance 2:1"
      // → section="Repentance", spec="2:1").
      const tm = tail.match(/^(.+?)\s+(\d[\d:.\-]*)\s*$/);
      if (tm) {
        const section = tm[1].trim();
        const specHe = formatVerseSpec(tm[2]);
        if (specHe) {
          return `${WORK_HE[m.key] || BOOK_HE[m.key] || TRACTATE_HE[m.key] || m.key}, ${section} ${specHe}`;
        }
      }
      return `${WORK_HE[m.key] || m.key}, ${tail}`;
    }
    return raw;
  }

  const m = matchBookPrefix(raw);
  if (!m) return raw;
  const hebrewBook = BOOK_HE[m.key] || TRACTATE_HE[m.key] || WORK_HE[m.key] || m.key;
  const rest = m.rest.trim();
  if (!rest) return hebrewBook;

  // Talmud daf — "5a" / "5b". Hebrew convention: "ה ע״א" / "ה ע״ב".
  const daf = rest.match(DAF_RE);
  if (daf) {
    const dafHe = numberToHebrew(parseInt(daf[1], 10));
    const amudHe = daf[2].toLowerCase() === "a" ? "ע\u05F4א" : "ע\u05F4ב";
    return `${hebrewBook} ${dafHe} ${amudHe}`;
  }

  const specHe = formatVerseSpec(rest);
  if (specHe) return `${hebrewBook} ${specHe}`;

  // Unknown trailing format — keep the Hebrew book name but leave the
  // tail untouched. Better than dropping the ref entirely.
  return `${hebrewBook} ${rest}`;
}
